import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { EXTERNAL_LINKS } from '@/consts/externalLinks.consts';
import { ZIndex } from '@/consts/z-indexes.consts';
import { I18N_KEYS } from '@/language/keys';
import {
  AppDialog,
  AppDialogActions,
  AppDialogContent,
  AppDialogHeader,
  AppDialogSection,
  AppDialogTitle,
} from '@/UI/AppDialog/AppDialog';
import { TextStdRg } from '@/UI/AppTypography/AppTypography';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { Link } from '@/UI/Link/Link';
import { FlexCol } from '@/UI/styles';
import { useTheme } from '@/UI/theme.exports';

interface WAFBlockingDialogProps {
  title: string;
  body: string;
  showBrowserHelp: boolean;
  onReload: () => void;
  isEmbedded: boolean;
}

/** The app-blocking WAF dialog whose only user action is Reload. */
export function WAFBlockingDialog({ title, body, showBrowserHelp, onReload, isEmbedded }: WAFBlockingDialogProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const theme = useTheme();
  // The dialog mounts above FigmaEmbedModeProvider, so ButtonContained's own useIsFigmaEmbedMode() read returns
  // its false default; pass the embed answer explicitly (undefined off-embed keeps FF_FIGMA_THEME honored),
  // otherwise the button renders weave-yellow over an embed's forced black/white chrome.
  const figmaThemeOverride = isEmbedded ? true : undefined;

  return (
    // aria-labelledby/header-id naming is MUI-path-only; the FPL AppDialog derives its accessible name from
    // its own Modal.Title, so do not add FPL naming here. zIndex: this is an app-global critical alert, so it
    // sits above app confirmation dialogs / snackbars, matching WAFInteractiveChallengeOverlay and ReloadAlert.
    // width matches the interactive overlay's card maxWidth so the two WAF modals read as one size.
    // slotProps paper font, MUI-path only (dropped on the FPL path): the MUI DS typography variants carry no
    // fontFamily, so before the app's CssBaseline mounts (the sign-in / auth-restore window) the title and body
    // would fall back to the UA serif. The FPL path resolves its font from the theme/StyleX, and forcing it there
    // would swap the tool page's loadable DM Sans for its unloadable Inter.
    <AppDialog
      open
      width={400}
      aria-labelledby={titleId}
      zIndex={ZIndex.Overlays.CRITICAL_SYSTEM_ALERT}
      slotProps={{ paper: { style: { fontFamily: theme.typography.fontFamily } } }}
    >
      <AppDialogSection>
        <AppDialogHeader id={titleId}>
          <AppDialogTitle>{title}</AppDialogTitle>
        </AppDialogHeader>
        <AppDialogContent>
          <FlexCol sx={{ gap: 1 }}>
            <TextStdRg>{body}</TextStdRg>
            {showBrowserHelp && (
              <TextStdRg>
                <Link href={EXTERNAL_LINKS.browserConfiguration} target="_blank" rel="noreferrer">
                  {t(I18N_KEYS.SECURITY_CHALLENGE.FAILURE_BROWSER_HELP)}
                </Link>
              </TextStdRg>
            )}
          </FlexCol>
        </AppDialogContent>
        <AppDialogActions>
          <ButtonContained isFigmaTheme={figmaThemeOverride} onClick={onReload}>
            {t(I18N_KEYS.SECURITY_CHALLENGE.RELOAD)}
          </ButtonContained>
        </AppDialogActions>
      </AppDialogSection>
    </AppDialog>
  );
}
